'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { Upload, Send, AlertCircle } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import DevicePicker from './DevicePicker'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import api from '../../lib/api'
import { getDeviceLabel } from '@/lib/devices'
import { devicesQueryOptions } from '@/lib/queries'
import { notify } from '@/lib/notify'

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 // 1 MB
const DEFAULT_MAX_ROWS = 50
const DEFAULT_INTERVAL_SECONDS = 5

// Rough SMS segment count: GSM-7 fits 160 chars (153 when split), anything else is UCS-2 at 70 (67 when split).
const GSM7 = /^[A-Za-z0-9 @£$¥èéùìòÇ\r\nØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\[~\]|€]*$/
const countSegments = (text: string) => {
  const single = GSM7.test(text) ? 160 : 70
  const multi = single === 160 ? 153 : 67
  return text.length <= single ? 1 : Math.ceil(text.length / multi)
}
const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds} seconds`
  const minutes = Math.round(seconds / 60)
  return minutes < 60 ? `${minutes} minutes` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}

export default function BulkSMSSend() {
  const [csvData, setCsvData] = useState<any[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [selectedColumn, setSelectedColumn] = useState<string>('')
  const [messageTemplate, setMessageTemplate] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const {
    data: currentSubscription,
  } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: () =>
      api
        .get('/billing/current-subscription')
        .then((res) => res.data?.data),
  })

  const maxRows = useMemo(() => {
    if (currentSubscription?.plan?.bulkSendLimit === -1) {
      return 9999
    }

    return currentSubscription?.plan?.bulkSendLimit || DEFAULT_MAX_ROWS
  }, [currentSubscription])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file.size > DEFAULT_MAX_FILE_SIZE) {
      setError('File size exceeds 1 MB limit.')
      return
    }

    Papa.parse(file, {
      complete: (results: any) => {
        if (results.data && results.data.length > 0) {
          if (results.data.length > maxRows) {
            setError(`CSV file exceeds ${maxRows} rows limit.`)
            return
          }
          setCsvData(results.data as any[])
          const headerRow = results.data[0] as Record<string, unknown>
          setColumns(Object.keys(headerRow))
          setError(null)
        } else {
          setError('CSV file is empty or invalid')
          setCsvData([])
          setColumns([])
        }
      },
      header: true,
      skipEmptyLines: true,
    })
  }, [maxRows])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  const validRows = useMemo(
    () => csvData.filter((row) => String(row[selectedColumn] ?? '').trim() !== ''),
    [csvData, selectedColumn]
  )
  const recipients = useMemo(
    () => validRows.map((row) => String(row[selectedColumn]).trim()),
    [validRows, selectedColumn]
  )
  const personalised = useMemo(
    () =>
      validRows.map((row) =>
        messageTemplate.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, key) => row[key.trim()] || '')
      ),
    [validRows, messageTemplate]
  )
  const totalSegments = useMemo(
    () => personalised.reduce((sum, m) => sum + countSegments(m), 0),
    [personalised]
  )

  const handleSendBulkSMS = async () => {
    await api.post(`/gateway/devices/${selectedDeviceId}/send-bulk-sms`, {
      deviceId: selectedDeviceId,
      recipients,
      message: messageTemplate,
      messages: personalised,
    })
  }

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')

  const { data: devices, isLoading: isLoadingDevices } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })
  const selectedDevice = devices?.find((d: any) => d.id === selectedDeviceId)
  const intervalSeconds = selectedDevice?.messageInterval || DEFAULT_INTERVAL_SECONDS

  useEffect(() => {
    if (!selectedDeviceId && devices?.length === 1) setSelectedDeviceId(devices[0].id)
  }, [devices, selectedDeviceId])

  const showToast = (title: string, description?: string, variant: 'default' | 'destructive' = 'default') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }

  const {
    mutate: sendBulkSMS,
    isPending: isSendingBulkSMS,
  } = useMutation({
    mutationFn: handleSendBulkSMS,
    onSuccess: () => {
      showToast(`Queued ${recipients.length} messages`, 'They go out one at a time from the sender. Check progress in History.')
    },
    onError: (error: any) => {
      showToast("Couldn't send to the list", error.response?.data?.message || error.message, 'destructive')
    },
  })

  const isStep2Disabled = csvData.length === 0
  const isStep3Disabled = isStep2Disabled || !selectedColumn || !messageTemplate || !selectedDeviceId || recipients.length === 0

  return (
    <div className='space-y-8'>
      <Card>
        <CardHeader>
          <CardTitle>Send to a list</CardTitle>
          <CardDescription>
            Upload a CSV, write your message, then review and send.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-8'>
          <section>
            <h2 className='text-lg font-semibold mb-2'>Step 1 of 3: upload your list</h2>
            <p className='text-sm text-gray-500 mb-4'>
              Upload a CSV file (up to 1 MB and {maxRows} rows) with one row per recipient.
            </p>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-300'
              }`}
            >
              <input {...getInputProps()} accept='.csv' />
              <Upload className='mx-auto h-12 w-12 text-gray-400' />
              <p className='mt-2'>
                Drag &amp; drop a CSV file here, or click to select one
              </p>
            </div>
            {error && (
              <Alert variant='destructive' className='mt-4'>
                <AlertCircle className='h-4 w-4' />
                <AlertTitle>Couldn't read the file</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {csvData.length > 0 && (
              <p className='mt-2 text-sm text-status-delivered'>
                {csvData.length} rows found.
              </p>
            )}
          </section>

          <section
            className={isStep2Disabled ? 'opacity-50 pointer-events-none' : ''}
          >
            <h2 className='text-lg font-semibold mb-2'>Step 2 of 3: write your message</h2>
            <p className='text-sm text-gray-500 mb-4'>
              Choose who sends it, which column holds the phone numbers, and write the message.
            </p>

            <div>
              <Label htmlFor='device-select'>Send from</Label>
              <DevicePicker
                id='device-select'
                devices={devices}
                value={selectedDeviceId}
                onChange={setSelectedDeviceId}
                isLoading={isLoadingDevices}
              />
            </div>

            <div className='space-y-4 mt-4'>
              <div>
                <Label htmlFor='recipient-column'>
                  Phone number column
                </Label>
                <Select
                  onValueChange={setSelectedColumn}
                  value={selectedColumn}
                  disabled={isStep2Disabled}
                >
                  <SelectTrigger id='recipient-column'>
                    <SelectValue placeholder='Choose a column' />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((column, index) => {
                      const safeValue = column && column.trim() !== '' ? column : `column-${index}`
                      const displayName = column && column.trim() !== '' ? column : `Column ${index + 1}`
                      return (
                        <SelectItem
                          key={`column-${index}`}
                          value={safeValue}
                        >
                          {displayName}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor='message-template'>Message Template</Label>
                <Textarea
                  id='message-template'
                  placeholder='Write your message. Use {{ column_name }} to insert a value from each row, for example {{ first_name }}.'
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  className='h-32'
                  disabled={isStep2Disabled}
                />
              </div>
            </div>
          </section>

          <section
            className={isStep3Disabled ? 'opacity-50 pointer-events-none' : ''}
          >
            <h2 className='text-lg font-semibold mb-2'>Step 3 of 3: review and send</h2>
            <dl className='grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm num'>
              <dt className='text-gray-600'>Recipients</dt>
              <dd>{recipients.length}{csvData.length > recipients.length && ` (${csvData.length - recipients.length} rows skipped: no phone number)`}</dd>
              <dt className='text-gray-600'>Sender</dt>
              <dd>{selectedDevice ? getDeviceLabel(selectedDevice) : 'None chosen'}</dd>
              <dt className='text-gray-600'>SMS segments</dt>
              <dd>{totalSegments} in total (a long message or one with emoji uses more than one segment)</dd>
              <dt className='text-gray-600'>Estimated time</dt>
              <dd>about {formatDuration(recipients.length * intervalSeconds)} ({intervalSeconds} seconds between messages)</dd>
            </dl>
            <p className='text-sm text-gray-600 mt-4 mb-1'>Sample, as the first recipient will receive it</p>
            <div className='rounded-md border bg-card p-4 whitespace-pre-wrap text-sm min-h-[3.5rem]'>
              <span className='font-mono text-xs block mb-2 text-gray-600'>{recipients[0]}</span>
              {personalised[0]}
            </div>
          </section>

          <Button
            className='w-full'
            disabled={isStep3Disabled || isSendingBulkSMS}
            onClick={() => sendBulkSMS()}
          >
            {isSendingBulkSMS ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Send className='mr-2 h-4 w-4' />
            )}
            {isSendingBulkSMS ? 'Sending…' : `Send to ${recipients.length} ${recipients.length === 1 ? 'number' : 'numbers'}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
